import React, { useContext } from "react";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import {useNavigate} from "react-router-dom";
import Box from "@mui/material/Box";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import moment from "moment";
import { ToasterContext } from "../../components/shared/Toaster/ToasterContext";
import { NotificationType } from "../../constants/global";

const BODY_FONT = '"Source Sans 3", sans-serif';
const MONO_FONT = '"Spline Sans Mono", monospace';

interface IStatusStyle {
  color: string;
  bg: string;
  dot: string;
}

const STATUS_STYLES: Record<string, IStatusStyle> = {
  APPROVED: { color: '#1E7B3C', bg: '#DFF2E5', dot: '#2E9E57' },
  PENDING: { color: '#1D4ED8', bg: '#EFF6FF', dot: '#2563EB' },
  LICENSE_UPLOADED: { color: '#1D4ED8', bg: '#EFF6FF', dot: '#2563EB' },
  REJECTED: { color: '#A02121', bg: '#F9E3E3', dot: '#C63A3A' },
  EXPIRED: { color: '#C2410C', bg: '#F4E7E0', dot: '#C2410C' },
};

const FALLBACK_STATUS_STYLE: IStatusStyle = { color: '#475569', bg: '#F1F5F9', dot: '#475569' };

const cellSx = {
  fontFamily: BODY_FONT,
  fontSize: '13.5px',
  color: '#1E293B',
  borderBottom: '1px solid #E2E8F0',
  py: 1.5,
};

const monoCellSx = {
  ...cellSx,
  fontFamily: MONO_FONT,
  fontSize: '12.5px',
  color: '#475569',
  whiteSpace: 'nowrap',
};

const copyIconSx = {
  fontSize: 16,
  cursor: 'pointer',
  color: '#475569',
  flexShrink: 0,
  transition: 'color .15s ease, transform .15s ease',
  '&:hover': {
    color: '#2563EB',
    transform: 'scale(1.1)',
  },
};

interface IPharmacyListItemProps {
  row: any;
}

const PharmacyListItem:React.FunctionComponent<IPharmacyListItemProps> = ({
  row,
}) => {
  const navigate = useNavigate();
  const { addToast } = useContext(ToasterContext);

  const statusStyle = STATUS_STYLES[row.status.Status.key] || FALLBACK_STATUS_STYLE;

  const handleCopy = (event: React.MouseEvent, value: string) => {
    event.stopPropagation();
    navigator.clipboard.writeText(value);
    addToast({ type: NotificationType.Success, title: 'Copied to Clipboard' });
  };

  return (
    <TableRow
      key={row.name}
      sx={{
        cursor: 'pointer',
        transition: 'background-color .15s ease',
        '&:hover': { backgroundColor: '#F1F5F9' },
        '&:last-child td, &:last-child th': { border: 0 },
      }}
      onClick={() => navigate(`/pharmacies/${row.id}`)}
    >
      <TableCell component="th" scope="row" sx={{ ...cellSx, fontWeight: 600, fontSize: 14 }}>
        {row.name}
      </TableCell>
      <TableCell align="left" sx={{ ...cellSx, color: '#475569' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {row.email}
          {row.email && (
            <ContentCopyIcon
              fontSize="small"
              onClick={(event) => handleCopy(event, row.email)}
              sx={copyIconSx}
            />
          )}
        </Box>
      </TableCell>
      <TableCell align="left" sx={monoCellSx}>{moment(row.created).format('MM/DD/YYYY hh:mm A')}</TableCell>
      <TableCell align="left" sx={monoCellSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {row.phone}
          {row.phone && (
            <ContentCopyIcon
              fontSize="small"
              onClick={(event) => handleCopy(event, row.phone)}
              sx={copyIconSx}
            />
          )}
        </Box>
      </TableCell>
      <TableCell align="left" sx={cellSx}>{row.user}</TableCell>
      <TableCell align="left" sx={monoCellSx}>{row.vacationDates || '—'}</TableCell>
      <TableCell align="left" sx={cellSx}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            px: '8px',
            py: '3px',
            borderRadius: '5px',
            fontFamily: MONO_FONT,
            fontSize: '10.5px',
            fontWeight: 600,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
            color: statusStyle.color,
            backgroundColor: statusStyle.bg,
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: statusStyle.dot,
              flexShrink: 0,
            }}
          />
          {row.status.Status.name}
        </Box>
      </TableCell>
    </TableRow>
  )
}

export default PharmacyListItem;
